import * as XLSX from 'xlsx';

// ════════════════════════════════════════════════════════════════════════════
// PARSER PURO — Plantilla de Migración Intellisis → SIG-RH (Fase 2, solo parseo)
// ════════════════════════════════════════════════════════════════════════════
// Sin fetch, sin backend, sin estado de React, sin efectos secundarios.
// Solo hace NORMALIZACIÓN TÉCNICA (texto/fechas/ceros) — no valida CURP/RFC,
// no valida catálogos, no compara contra EMPLEADOS, no clasifica filas.
// Eso pertenece a la fase de dry-run del backend (todavía no implementada).
//
// ADVERTENCIA DE DUPLICACIÓN DE CONTRATO (riesgo aceptado explícitamente):
// COLUMNAS_EMPLEADOS_IMPORTAR de este archivo DEBE ser un espejo exacto,
// carácter por carácter y en el mismo orden, del arreglo del mismo nombre en
// AppsScript_Backend.gs (fuente de verdad técnica del contrato). Apps Script
// (.gs) y este proyecto CRA (ES modules empaquetados por webpack) no pueden
// compartir literalmente el mismo archivo de origen sin una herramienta de
// generación/sincronización que hoy no existe — se acepta la duplicación en
// vez de forzar ese refactor. La comparación entre ambos arreglos se verifica
// con scripts/verificar-contrato-importacion.js, que debe ejecutarse cada vez
// que cualquiera de los dos arreglos cambie.
export const COLUMNAS_EMPLEADOS_IMPORTAR = [
  'ID_Empleado', 'Apellido_Paterno', 'Apellido_Materno', 'Nombres',
  'ID_Sucursal', 'ID_Ubicacion', 'ID_Centro', 'Nodo_ID', 'ID_Puesto', 'ID_Turno',
  'CURP', 'RFC', 'NSS', 'SDI', 'Salario_Diario', 'CLABE',
  'Fecha_Nacimiento', 'Fecha_Ingreso', 'Sexo', 'Estado_Civil', 'Escolaridad',
  'Telefono', 'Email', 'Tiene_Hijos', 'Numero_Hijos',
  'Ruta_Transporte', 'Punto_Ruta_ID',
  'Beneficiario_Nombre', 'Beneficiario_Fecha_Nacimiento', 'Beneficiario_Lugar_Nacimiento',
  'Beneficiario_Parentesco', 'Beneficiario_Telefono',
  'Domicilio_Calle', 'Domicilio_Numero_Ext', 'Domicilio_Numero_Int', 'Domicilio_Colonia',
  'Domicilio_Municipio', 'Domicilio_Estado', 'Domicilio_CP', 'CP_SAT',
  'Contacto_Emergencia_Nombre', 'Contacto_Emergencia_Parentesco', 'Contacto_Emergencia_Telefono',
  'Tipo_Empleado', 'Estado_Nacimiento'
];

export const HOJA_DATOS = 'EMPLEADOS_IMPORTAR';
export const HOJAS_REQUERIDAS = ['EMPLEADOS_IMPORTAR', 'CATALOGOS_SIGRH', 'INSTRUCCIONES'];

// Columnas que se interpretan como fecha (YYYY-MM-DD acordado). El resto de
// las 45 columnas, salvo las numéricas de abajo, se tratan como texto genérico.
const COLUMNAS_FECHA = ['Fecha_Nacimiento', 'Fecha_Ingreso', 'Beneficiario_Fecha_Nacimiento'];

// Únicas columnas verdaderamente numéricas — mismo criterio que
// COLUMNAS_NUMERICAS_EMPLEADOS_IMPORTAR en AppsScript_Backend.gs.
const COLUMNAS_NUMERICAS = ['SDI', 'Salario_Diario', 'Numero_Hijos'];

/**
 * Punto de entrada real (navegador): recibe un File del input de carga.
 * Delega toda la lógica a parsearWorkbookDesdeBuffer, que es síncrona y pura
 * (facilita probarla con workbooks construidos en memoria, sin depender de
 * File.prototype.arrayBuffer en el entorno de pruebas).
 */
export async function parsearPlantillaXLSX(file) {
  const buffer = await file.arrayBuffer();
  return parsearWorkbookDesdeBuffer(buffer);
}

/**
 * Núcleo puro del parser. Sin async, sin I/O, sin dependencias del navegador.
 * @param {ArrayBuffer|Uint8Array} buffer
 */
export function parsearWorkbookDesdeBuffer(buffer) {
  let workbook;
  try {
    workbook = XLSX.read(buffer, { type: 'array', cellDates: true, cellText: true });
  } catch (e) {
    return construirErrorEstructural(
      'ARCHIVO_ILEGIBLE',
      'No se pudo leer el archivo como XLSX.',
      String((e && e.message) || e)
    );
  }

  const faltantes = HOJAS_REQUERIDAS.filter(h => !workbook.SheetNames.includes(h));
  if (faltantes.length > 0) {
    return construirErrorEstructural(
      'HOJA_FALTANTE',
      `Falta(n) la(s) hoja(s) requerida(s): ${faltantes.join(', ')}.`,
      { faltantes, hojasEncontradas: workbook.SheetNames }
    );
  }

  const hoja = workbook.Sheets[HOJA_DATOS];
  if (!hoja || !hoja['!ref']) {
    return construirErrorEstructural(
      'SIN_ENCABEZADOS',
      'La hoja EMPLEADOS_IMPORTAR está vacía.',
      null
    );
  }

  const rango = XLSX.utils.decode_range(hoja['!ref']);
  const totalColsHoja = rango.e.c + 1;
  const totalFilasHoja = rango.e.r + 1;

  // Encabezados: fila 0, control manual (no sheet_to_json automático) para
  // poder validar cantidad/orden/duplicados nosotros mismos.
  const encabezados = [];
  for (let c = 0; c < totalColsHoja; c++) {
    const cell = hoja[XLSX.utils.encode_cell({ r: 0, c })];
    encabezados.push(cell && cell.v !== undefined && cell.v !== null ? String(cell.v).trim() : '');
  }

  const errorEncabezados = validarEncabezados(encabezados);
  if (errorEncabezados) return errorEncabezados;

  const filas = [];
  for (let r = 1; r < totalFilasHoja; r++) {
    if (esFilaVacia(hoja, r, COLUMNAS_EMPLEADOS_IMPORTAR.length)) continue;

    const datos = {};
    for (let c = 0; c < COLUMNAS_EMPLEADOS_IMPORTAR.length; c++) {
      const nombreCol = COLUMNAS_EMPLEADOS_IMPORTAR[c];
      const cell = hoja[XLSX.utils.encode_cell({ r, c })];
      datos[nombreCol] = normalizarCelda(nombreCol, cell);
    }
    // r es 0-based y r=0 es el encabezado (fila 1 de Excel) → fila real = r+1.
    filas.push({ fila_excel: r + 1, datos });
  }

  return {
    success: true,
    error_estructural: null,
    estructura: {
      hojas: workbook.SheetNames,
      columnas: COLUMNAS_EMPLEADOS_IMPORTAR.length,
      filas_datos: filas.length
    },
    filas
  };
}

function validarEncabezados(encabezados) {
  if (encabezados.length !== COLUMNAS_EMPLEADOS_IMPORTAR.length) {
    return construirErrorEstructural(
      'ENCABEZADOS_CANTIDAD_INVALIDA',
      `Se esperaban ${COLUMNAS_EMPLEADOS_IMPORTAR.length} columnas, se encontraron ${encabezados.length}.`,
      { esperado: COLUMNAS_EMPLEADOS_IMPORTAR.length, encontrado: encabezados.length, encabezados }
    );
  }

  const duplicados = [...new Set(encabezados.filter((h, idx) => encabezados.indexOf(h) !== idx))];
  if (duplicados.length > 0) {
    return construirErrorEstructural(
      'ENCABEZADO_DUPLICADO',
      `Encabezado(s) duplicado(s): ${duplicados.join(', ')}.`,
      { duplicados }
    );
  }

  for (let i = 0; i < COLUMNAS_EMPLEADOS_IMPORTAR.length; i++) {
    if (encabezados[i] !== COLUMNAS_EMPLEADOS_IMPORTAR[i]) {
      return construirErrorEstructural(
        'ENCABEZADO_INCORRECTO',
        `Columna ${i + 1}: se esperaba "${COLUMNAS_EMPLEADOS_IMPORTAR[i]}", se encontró "${encabezados[i]}".`,
        { posicion: i + 1, esperado: COLUMNAS_EMPLEADOS_IMPORTAR[i], encontrado: encabezados[i] }
      );
    }
  }

  return null;
}

function esFilaVacia(hoja, filaIdx, totalCols) {
  for (let c = 0; c < totalCols; c++) {
    const cell = hoja[XLSX.utils.encode_cell({ r: filaIdx, c })];
    if (cell !== undefined && cell.v !== undefined && cell.v !== null && String(cell.v).trim() !== '') {
      return false;
    }
  }
  return true;
}

function normalizarCelda(nombreColumna, cell) {
  if (COLUMNAS_FECHA.includes(nombreColumna)) return normalizarCeldaFecha(cell);
  if (COLUMNAS_NUMERICAS.includes(nombreColumna)) return normalizarCeldaNumerica(cell);
  return normalizarCeldaTexto(cell);
}

// Texto genérico — cubre también las columnas sensibles a ceros iniciales
// (ID_Empleado, NSS, CLABE, Telefono, Domicilio_CP, CP_SAT, teléfonos de
// contacto/beneficiario, IDs de catálogo): siempre se devuelven como string,
// preservando exactamente lo que ya trae la celda. Si Excel ya destruyó un
// cero inicial antes de llegar aquí, NO se reconstruye.
function normalizarCeldaTexto(cell) {
  if (cell === undefined || cell === null || cell.v === undefined || cell.v === null) return '';
  if (cell.t === 's') return String(cell.v).trim();
  if (cell.t === 'n') return String(cell.v);
  if (cell.t === 'd') return formatearFechaUTC(cell.v);
  if (cell.t === 'b') return cell.v ? 'TRUE' : 'FALSE';
  return cell.w !== undefined ? String(cell.w).trim() : '';
}

// SDI / Salario_Diario / Numero_Hijos — se devuelven como número JS cuando la
// celda es genuinamente numérica, preservando 0 (nunca se convierte a '').
// No se redondea, no se valida rango — eso es decisión del backend futuro.
function normalizarCeldaNumerica(cell) {
  if (cell === undefined || cell === null || cell.v === undefined || cell.v === null) return '';
  if (cell.t === 'n') return cell.v;
  if (cell.t === 's') {
    const texto = String(cell.v).trim();
    if (texto === '') return '';
    const num = Number(texto);
    // Si el texto no es un número válido, se conserva tal cual para que el
    // backend futuro lo marque inválido — el parser no decide eso.
    return Number.isNaN(num) ? texto : num;
  }
  return cell.w !== undefined ? cell.w : '';
}

// Fecha_Nacimiento / Fecha_Ingreso / Beneficiario_Fecha_Nacimiento.
// A) Texto "YYYY-MM-DD" → se preserva tal cual, sin tocar.
// B/C) Celda de fecha real de Excel o serial reconocido como fecha
//      (cell.t === 'd', gracias a cellDates:true en XLSX.read) → se
//      normaliza determinísticamente a YYYY-MM-DD usando componentes UTC
//      (SheetJS construye estas fechas en UTC para evitar corrimiento).
// D) Cualquier otro texto (ej. "08/09/2026") → se preserva como texto crudo,
//    sin interpretar — el backend futuro la marcará inválida si no cumple
//    el formato acordado. Nunca se adivina un formato ambiguo.
function normalizarCeldaFecha(cell) {
  if (cell === undefined || cell === null || cell.v === undefined || cell.v === null) return '';
  if (cell.t === 'd') return formatearFechaUTC(cell.v);
  if (cell.t === 's') return String(cell.v).trim();
  if (cell.t === 'n') return String(cell.v); // numérico sin formato de fecha reconocido — se conserva crudo
  return cell.w !== undefined ? String(cell.w).trim() : '';
}

function formatearFechaUTC(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function construirErrorEstructural(codigo, mensaje, detalle) {
  return {
    success: false,
    error_estructural: { codigo, mensaje, detalle: detalle !== undefined ? detalle : null },
    estructura: null,
    filas: []
  };
}
